import React, { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import useOfficersForm from "./useOfficersForm";
import { getData } from "country-list";
import { RadioButton } from "primereact/radiobutton";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import { Calendar } from "primereact/calendar";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";

const OfficerRegistration = ({
  btnToggle,
  setBtnToggle,
  formDetails,
  setFormDetails,
  type,
  regData,
  setRegData,
  isEditing,
  isNewOfficer,
  setFormResetRef,
}) => {
  const { roles, actingAsOptions, officerConfig } = useOfficersForm({
    type,
    isNewOfficer,
  });
  const countries = getData();
  const resolveValue = (value, options) =>
    value && typeof value === "object"
      ? value
      : options?.find((option) => option.code === value);

  const actingAsValue = resolveValue(regData?.acting_as, actingAsOptions);
  const countryValue = resolveValue(regData?.country, countries);
  const roleValue = resolveValue(regData?.role, roles);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: regData?.name || "",
      other_name: regData?.other_name || "",
      last_name: regData?.last_name || "",
      preferred_name: regData?.preferred_name || "",
      role: roleValue || "",
      title: regData?.title || "",
      email: regData?.email || "",
      country: countryValue || "",
      phone_code: regData?.phone_code || "",
      phone: regData?.phone || "",
      expiration_date: regData?.expiration_date || "",
      acting_as: actingAsValue || "",
    },
  });

  useEffect(() => {
    setBtnToggle(!isValid);
  }, [isValid, setBtnToggle]);

  const formDetailsRef = useRef(formDetails);
  useEffect(() => {
    if (
      JSON.stringify(formDetailsRef.current) !== JSON.stringify(formDetails)
    ) {
      reset(formDetails);
      formDetailsRef.current = formDetails;
    }
  }, [formDetails, reset]);

  useEffect(() => {
    if (setFormResetRef) {
      setFormResetRef((data) => {
        reset(data || formDetailsRef.current);
        formDetailsRef.current = data || formDetailsRef.current;
      });
    }
  }, [setFormResetRef, reset]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (JSON.stringify(formDetailsRef.current) !== JSON.stringify(value)) {
        formDetailsRef.current = value;
        setFormDetails(value);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setFormDetails]);

  const onSubmit = (data) => {
    setFormDetails(data);
    setRegData(data);
  };

  const roleOptionsTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            typeof regData?.role === "object"
              ? regData?.role.code === option.code
              : regData?.role === option.code
          }
        />
        <span>{option.name}</span>
      </div>
    );
  };

  const actingAsOptionsTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            regData?.acting_as && typeof regData?.acting_as === "object"
              ? regData?.acting_as.code === option.code
              : regData?.acting_as === option.code
          }
        />
        <span>{option.name}</span>
      </div>
    );
  };

  return (
    <>
      {isNewOfficer && (
        <div className="formgrid grid">
          <div className="field col-12">
            <p className="add-new-entity-heading mb-2">
              {officerConfig?.formHeader}
            </p>
            <p className="input-label m-0">{officerConfig?.formDescription}</p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="formgrid grid">
          <div className="field col-6">
            <label className="contact-label mt-2">
              Given Name(s) *{" "}
              <span id="given-name-info" className="pi pi-info-circle"></span>
              <Tooltip
                target="#given-name-info"
                content="This is usually the person's first name or the name they usually go by."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="name"
              control={control}
              defaultValues
              rules={{
                required: "Given Name is required.",
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, name: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter given name(s)"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.name ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.name && (
              <small className="error-message">{errors.name.message}</small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Other Name(s)
              <span
                id="other-name-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#other-name-info"
                content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="other_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, other_name: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter any other names, such as middle names or clan names"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.other_name ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.other_name && (
              <small className="error-message">
                {errors.other_name.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Surname(s)
              <span
                id="surname-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#surname-info"
                content="This is usually a family name or last name. If employee has more than one, include them here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="last_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, last_name: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter surname(s)"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.last_name ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.last_name && (
              <small className="error-message">
                {errors.last_name.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">
              Preferred Name(s)
              <span
                id="preferred-name-info"
                className="pi pi-info-circle info-circle-span"
              ></span>
              <Tooltip
                target="#preferred-name-info"
                content="If the employee wants to be called by a different name than their given name, put that here."
                position="right"
                className="custom-tooltip tooltip-custom-format"
              />
            </label>
            <Controller
              name="preferred_name"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[A-Za-z\s]+$/,
                  message: "Special characters are not allowed.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, preferred_name: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Preferred name or nickname"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.preferred_name ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.preferred_name && (
              <small className="error-message">
                {errors.preferred_name.message}
              </small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">Role *</label>
            <Controller
              name="role"
              control={control}
              rules={{
                required: "Role is required.",
                validate: (value) => {
                  if (!value || !value.code) return "Role is required.";
                  return true;
                },
              }}
              render={({ field }) => {
                const roleObject =
                  typeof regData?.role === "object"
                    ? regData?.role
                    : roles?.find((role) => role.code === regData?.role);
                const isDisabled = !roles.length;
                return (
                  <Dropdown
                    {...field}
                    value={roleObject || field.value}
                    onChange={(e) => {
                      const newValue = e.value;
                      setRegData({ ...regData, role: newValue });
                      field.onChange(newValue);
                    }}
                    options={
                      roleObject && !roles.length
                        ? [roleObject]
                        : roles.length
                          ? roles
                          : [roleObject].filter(Boolean)
                    }
                    optionLabel="name"
                    itemTemplate={roleOptionsTemplate}
                    placeholder="Select role"
                    className={`officer-dropdown-field w-full ${errors.role ? "error" : ""}`}
                    disabled={isDisabled || !isEditing}
                  />
                );
              }}
            />
            {errors.role && (
              <small className="error-message">{errors.role.message}</small>
            )}
          </div>
          <div className="field col-6">
            <label className="contact-label mt-2">Title</label>
            <Controller
              name="title"
              control={control}
              defaultValues
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, title: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter title"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.role ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.title && (
              <small className="error-message">{errors.title.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Contact Email *</label>
            <Controller
              name="email"
              control={control}
              defaultValues
              rules={{
                required: "Email Address is required.",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                  message: "Email is invalid.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, email: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter email address"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.email ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.email && (
              <small className="error-message">{errors.email.message}</small>
            )}
          </div>
          <div className="field col-12">
            <label className="contact-label mt-2">Country *</label>
            <Controller
              name="country"
              control={control}
              rules={{
                required: "Country is required.",
                validate: (value) => {
                  if (!value || !value.code) return "Country is required.";
                  return true;
                },
              }}
              render={({ field }) => {
                const countryObject =
                  typeof regData?.country === "object"
                    ? regData?.country
                    : countries?.find(
                        (country) => country.code === regData?.country,
                      );
                const isDisabled = !countries.length;
                return (
                  <Dropdown
                    {...field}
                    value={countryObject || field.value}
                    onChange={(e) => {
                      const newValue = e.value;
                      setRegData({ ...regData, country: newValue });
                      field.onChange(newValue);
                    }}
                    options={
                      countryObject && !countries.length
                        ? [countryObject]
                        : countries.length
                          ? countries
                          : [countryObject].filter(Boolean)
                    }
                    optionLabel="name"
                    placeholder="Select country"
                    className={`officer-dropdown-field w-full ${errors.country ? "error" : ""}`}
                    disabled={isDisabled || !isEditing}
                    itemTemplate={(option) => (
                      <div className="flex align-items-center">
                        <ReactCountryFlag
                          countryCode={option.code}
                          svg
                          className="flag-svg-icon"
                        />
                        <span>{option.name}</span>
                      </div>
                    )}
                  />
                );
              }}
            />
            {errors.country && (
              <small className="error-message">{errors.country.message}</small>
            )}
          </div>
          <div className="field col-12 mb-0">
            <label className="contact-label mt-2">Phone Number</label>
          </div>
          <div className="field col-3">
            <Controller
              name="phone_code"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone code must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, phone_code: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Country code"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone_code ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.phone_code && (
              <small className="error-message">
                {errors.phone_code.message}
              </small>
            )}
          </div>
          <div className="field col-9">
            <Controller
              name="phone"
              control={control}
              defaultValues
              rules={{
                pattern: {
                  value: /^[0-9]+$/,
                  message: "Phone number must contain only digits.",
                },
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRegData({ ...regData, phone: newValue });
                    field.onChange(newValue);
                  }}
                  type="text"
                  placeholder="Enter phone number"
                  className={`input-field appearance-none outline-none w-full ${
                    errors.phone ? "error" : ""
                  }`}
                  disabled={!isEditing}
                />
              )}
            />
            {errors.phone && (
              <small className="error-message">{errors.phone.message}</small>
            )}
          </div>
          {type === "power-of-attorney" && (
            <>
              <div className="field col-12">
                <label className="contact-label mt-2">Expiration Date</label>
                <div className="calendar-container">
                  <Controller
                    name="expiration_date"
                    control={control}
                    defaultValues
                    render={({ field }) => (
                      <>
                        <Calendar
                          {...field}
                          value={
                            field.value &&
                            removeTimezoneFromDateString(field.value)
                          }
                          onChange={(e) => {
                            const newDate = formatDateToISOStartOfDay(e.value);
                            setRegData({
                              ...regData,
                              expiration_date: newDate,
                            });
                            field.onChange(newDate);
                          }}
                          placeholder="Select a date"
                          minDate={new Date()}
                          className="calendar-field w-full"
                          disabled={!isEditing}
                        />
                        <span className="pi pi-calendar calendar-icon"></span>
                      </>
                    )}
                  />
                </div>
              </div>
              <div className="field col-12">
                <label className="contact-label mt-2">Acting as</label>
                <Controller
                  name="acting_as"
                  control={control}
                  defaultValues
                  render={({ field }) => {
                    const actingAsObject =
                      typeof regData?.acting_as === "object"
                        ? regData?.acting_as
                        : actingAsOptions?.find(
                            (actingAs) => actingAs.code === regData?.acting_as,
                          );
                    const isDisabled = !actingAsOptions.length;
                    return (
                      <Dropdown
                        {...field}
                        value={actingAsObject || field.value}
                        onChange={(e) => {
                          const newValue = e.value;
                          setRegData({
                            ...regData,
                            acting_as: newValue,
                          });
                          field.onChange(newValue);
                        }}
                        options={
                          actingAsObject && !actingAsOptions.length
                            ? [actingAsObject]
                            : actingAsOptions.length
                              ? actingAsOptions
                              : [actingAsObject].filter(Boolean)
                        }
                        optionLabel="name"
                        placeholder="Select an option"
                        itemTemplate={actingAsOptionsTemplate}
                        className="officer-dropdown-field w-full"
                        disabled={isDisabled || !isEditing}
                      />
                    );
                  }}
                />
              </div>
            </>
          )}
        </div>
      </form>
    </>
  );
};

OfficerRegistration.propTypes = {
  setShowDialog: PropTypes.func,
  type: PropTypes.string,
  regData: PropTypes.object,
  setRegData: PropTypes.func,
  btnToggle: PropTypes.bool,
  setBtnToggle: PropTypes.func,
  formDetails: PropTypes.object,
  setFormDetails: PropTypes.func,
};

export default OfficerRegistration;
